import { reverse } from "@reactivated";

import { IAddress } from "../models/address";
import { IOrderResponse, IPayment, IPaymentStates } from "../models/checkout";
import { CSRF_HEADER, fetchJson, getCSRFToken } from "../utils/ajax";
import { PromiseMutex } from "../utils/mutex";

export const listEnabledPaymentMethods = async (): Promise<string[]> => {
    const mutex = new PromiseMutex<string[]>("list-enabled-payment-methods");
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson<Record<string, unknown>>(
            reverse("api-checkout-payment-methods"),
        ).then((body) => {
            return Object.keys(body);
        });
        mutex.setPromise(loading);
    }
    return loading;
};

export const saveEmailAddress = async (
    email: string,
): Promise<{ owner_email: string }> => {
    return fetchJson(reverse("api-basket-email"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify({ owner_email: email }),
    });
};

export const loadEmailAddress = async (): Promise<{ owner_email: string }> => {
    const mutex = new PromiseMutex<{ owner_email: string }>(
        "load-basket-email-address",
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson(reverse("api-basket-email"));
        mutex.setPromise(loading);
    }
    return loading;
};

export const saveShippingAddress = async (
    shippingAddress: IAddress,
    bypassAddressValidation = false,
): Promise<IAddress> => {
    const data = {
        ...shippingAddress,
        bypass_validation: bypassAddressValidation ? "t" : "f",
    };
    return fetchJson(reverse("checkout-shipping-address"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify(data),
    });
};

export const loadShippingAddress = async (): Promise<IAddress> => {
    const mutex = new PromiseMutex<IAddress>("load-shipping-address");
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson(reverse("checkout-shipping-address"));
        mutex.setPromise(loading);
    }
    return loading;
};

export const clearShippingAddress = async (): Promise<void> => {
    await fetchJson(reverse("checkout-shipping-address"), {
        method: "DELETE",
        headers: {
            [CSRF_HEADER]: await getCSRFToken(),
        },
    });
};

export const saveBillingAddress = async (
    billingAddress: IAddress,
    bypassAddressValidation = false,
): Promise<IAddress> => {
    const data = {
        ...billingAddress,
        bypass_validation: bypassAddressValidation ? "t" : "f",
    };
    return fetchJson(reverse("checkout-billing-address"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify(data),
    });
};

export const loadBillingAddress = async (): Promise<IAddress> => {
    const mutex = new PromiseMutex<IAddress>("load-billing-address");
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson(reverse("checkout-billing-address"));
        mutex.setPromise(loading);
    }
    return loading;
};

export const clearBillingAddress = async (): Promise<void> => {
    await fetchJson(reverse("checkout-billing-address"), {
        method: "DELETE",
        headers: {
            [CSRF_HEADER]: await getCSRFToken(),
        },
    });
};

export const placeOrder = async (
    basketURL: string,
    shippingAddress: IAddress,
    shippingMethodCode: string,
    billingAddress: IAddress,
    payment: IPayment,
    guestEmail?: string,
    recaptcha?: string,
): Promise<IOrderResponse> => {
    const data = {
        guest_email: guestEmail,
        basket: basketURL,
        shipping_address: shippingAddress,
        shipping_method_code: shippingMethodCode,
        billing_address: billingAddress,
        payment: payment,
        recaptcha: recaptcha,
    };

    return fetchJson(reverse("api-checkout"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify(data),
    });
};

export const completeDeferredPayment = async (
    basketToken: string,
    orderToken: string,
    payment: IPayment,
): Promise<IOrderResponse> => {
    const data = {
        order: orderToken,
        payment: payment,
    };
    const params = new URLSearchParams({ "basket-token": basketToken });
    return fetchJson(`${reverse("api-complete-deferred-payment")}?${params}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify(data),
    });
};

export const checkPaymentStates = async (): Promise<IPaymentStates> => {
    // TODO: Use reverse here once url name is fixed. rreverse("api-payment-states-list")
    return fetchJson("/api/checkout/payment-states/");
};
